import { useEffect } from "react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import api from "../../lib/apiClient";

// Three staggered confetti bursts in the brand palette. Respects
// prefers-reduced-motion (those users still get the toast).
const fireSubscribeCelebration = () => {
  if (typeof window === "undefined") return;
  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  if (reduced) return;
  const defaults = { startVelocity: 35, spread: 60, ticks: 100, zIndex: 9999 };
  const colors = ["#10b981", "#f59e0b", "#fb7185", "#a78bfa", "#facc15"];
  confetti({ ...defaults, particleCount: 80, origin: { x: 0.5, y: 0.3 }, colors });
  setTimeout(() => {
    confetti({ ...defaults, particleCount: 50, angle: 60, spread: 75, origin: { x: 0, y: 0.55 }, colors });
    confetti({ ...defaults, particleCount: 50, angle: 120, spread: 75, origin: { x: 1, y: 0.55 }, colors });
  }, 180);
};

/**
 * usePaymentRedirectHandler — wires up the two post-Stripe redirect
 * handlers the Dashboard owns:
 *
 *   1. `?payment_session_id=…` (legacy one-time path: topup, commission,
 *      gift) — polls `/payments/status/{id}` until paid+fulfilled, then
 *      fires the appropriate toast and refreshes user credits.
 *
 *   2. `?subscription_session_id=…` (recurring path) — the webhook does
 *      the heavy lifting (grants credits, sets tier) so we just celebrate
 *      + refresh user state. Retries refreshUser a few times in case the
 *      webhook is slightly behind the redirect.
 *
 *   3. `?payment_cancelled=1` — bare toast, no API calls.
 *
 * All three clean their respective URL params after handling so the
 * effect doesn't re-fire on subsequent renders. After a successful
 * recurring subscribe we also set `?x_prompt=1` so the X Follow & Earn
 * panel auto-opens the list picker.
 *
 * Side-effect-only hook (returns nothing).
 */
export function usePaymentRedirectHandler({ dashboardSearchParams, setDashboardSearchParams, token, refreshUser }) {
  // One-time payments (topup / commission / gift) — polls until fulfilled.
  useEffect(() => {
    const sessionId = dashboardSearchParams.get("payment_session_id");
    const cancelled = dashboardSearchParams.get("payment_cancelled");
    if (cancelled) {
      toast.info("Payment cancelled — no charge was made.");
      dashboardSearchParams.delete("payment_cancelled");
      setDashboardSearchParams(dashboardSearchParams, { replace: true });
      return;
    }
    if (!sessionId || !token) return;
    let cancelledFlag = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelledFlag) return;
      attempts += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        const data = res.data;
        if (data.status === "paid" && data.fulfilled) {
          const intent = data.intent;
          const credits = data.metadata?.credits_to_grant;
          if (intent === "subscribe") {
            toast.success("Subscription active! Monthly credits added.");
            fireSubscribeCelebration();
            try { localStorage.removeItem("globaleyes_referrer"); } catch (_e) { /* ignore */ }
            try { localStorage.removeItem("globaleyes_referrer_pending"); } catch (_e) { /* ignore */ }
          } else if (intent === "commission") {
            toast.success("Commission posted! Producers can lock it now.");
          } else if (intent === "gift") {
            toast.success("Gift sent! Recipient credited.");
          } else {
            toast.success(credits ? `+${credits} credits added!` : "Payment successful!");
          }
          await refreshUser?.();
          dashboardSearchParams.delete("payment_session_id");
          setDashboardSearchParams(dashboardSearchParams, { replace: true });
          return;
        }
        if (data.status === "expired" || data.status === "failed") {
          toast.error(`Payment ${data.status}.`);
          dashboardSearchParams.delete("payment_session_id");
          setDashboardSearchParams(dashboardSearchParams, { replace: true });
          return;
        }
        if (attempts < 8) setTimeout(poll, 2000);
        else toast.info("Payment is still processing — refresh in a moment.");
      } catch (err) {
        console.error("payment poll error", err);
        try {
          if (typeof window !== "undefined" && window.Sentry) {
            window.Sentry.captureException(err, { tags: { area: "payment_poll" } });
          }
        } catch (_e) { /* Sentry unavailable — already logged to console */ }
      }
    };
    toast.info("Confirming payment with Stripe…");
    poll();
    return () => { cancelledFlag = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardSearchParams.get("payment_session_id"), token]);

  // Recurring-subscribe path. Webhook does the heavy lifting; we just
  // celebrate + refresh user state. No polling on `/payments/status`
  // because the recurring flow doesn't write a payment_transactions row
  // (Stripe Subscriptions live in their own object world).
  useEffect(() => {
    const sessionId = dashboardSearchParams.get("subscription_session_id");
    if (!sessionId || !token) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        await refreshUser?.();
      } catch (err) {
        // Tolerate a single refresh failure mid-poll — the next attempt
        // will likely succeed. Surface to Sentry for visibility instead
        // of silent swallow.
        try {
          if (typeof window !== "undefined" && window.Sentry) {
            window.Sentry.captureException(err, {
              tags: { area: "subscription_poll_refresh" },
            });
          }
        } catch (_e) { /* Sentry unavailable */ }
      }
      if (attempts === 1) {
        // Refresh first so the modal can read the updated tier; then
        // fire the success event that the SubscriptionSuccessModal
        // listens for. A toast as fallback in case the modal is
        // unmounted (e.g. during HMR / quick navigations) so the user
        // still gets SOME confirmation.
        toast.success("Subscription active — credits added!", { duration: 5000 });
        fireSubscribeCelebration();
        try {
          window.dispatchEvent(new CustomEvent("subscription-success"));
        } catch (_e) { /* SSR safety */ }
      }
      if (attempts < 6) {
        setTimeout(poll, 1500);
        return;
      }
      dashboardSearchParams.delete("subscription_session_id");
      // Right after a successful Producer subscription, surface the X
      // "Follow & Earn" prompt (the panel itself only renders for
      // Producers, so this is a no-op for other tiers).
      dashboardSearchParams.set("x_prompt", "1");
      setDashboardSearchParams(dashboardSearchParams, { replace: true });
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardSearchParams.get("subscription_session_id"), token]);
}
